import { storeToRefs } from 'pinia'
import { watch, type Ref } from 'vue'
import { useCookie, useOidcAuth, useState } from '#imports'
import { useMonOrgaColorMode } from '~/composables/ui/useMonOrgaColorMode'
import type { OnboardingStateMap } from '~/onboarding/types'
import { useUserPrefsStore } from '~~/stores/userPrefs'
import { defaultUserPreferences } from '~/utils/userPreferences/defaults'
import { runHydrate, withLatestOnboarding } from '~/utils/userPreferences/hydrateUserPreferences'
import { putUserPreferencesToApi, savePrefsToApi } from '~/utils/userPreferences/userPreferencesApi'
import {
  USER_PREFS_COOKIE_KEY,
  USER_PREFS_COOKIE_MAX_AGE_SECONDS,
  USER_PREFS_STORAGE_KEY,
  preferNewestLocalPrefs,
  readPrefsFromCookie,
  readPrefsFromLocalStorage,
  writePrefsToCookie,
  writePrefsToLocalStorage,
} from '~/utils/userPreferences/userPreferencesStorage'
import type {
  ColorModePreference,
  KeyboardShortcut,
  UserPreferencesPayload,
  UserShortcuts,
} from '~/utils/userPreferences/types'

export type { ColorModePreference, KeyboardShortcut, UserPreferencesPayload, UserShortcuts }
export { USER_PREFS_COOKIE_KEY, USER_PREFS_STORAGE_KEY, defaultUserPreferences }

let clientHydrateInFlight: Promise<void> | null = null

type UserPreferencesRuntime = {
  colorMode: ReturnType<typeof useMonOrgaColorMode>
  loggedIn: ReturnType<typeof useOidcAuth>['loggedIn']
  user: ReturnType<typeof useOidcAuth>['user']
  userPrefsStore: ReturnType<typeof useUserPrefsStore>
  prefs: ReturnType<typeof storeToRefs<ReturnType<typeof useUserPrefsStore>>>['prefs']
  prefsCookie: Ref<UserPreferencesPayload | null>
}

function applyColorModeFromPrefs(runtime: UserPreferencesRuntime, preference: ColorModePreference): void {
  if (!import.meta.client) {
    return
  }
  runtime.colorMode.preference = preference
}

function persistPrefs(runtime: UserPreferencesRuntime, nextPrefs: UserPreferencesPayload): void {
  const prefsToStore = withLatestOnboarding(nextPrefs)
  writePrefsToLocalStorage(prefsToStore)
  writePrefsToCookie(prefsToStore, (value) => {
    runtime.prefsCookie.value = value
  })
  if (prefsToStore !== nextPrefs) {
    runtime.userPrefsStore.setPrefs(prefsToStore)
  }
  void savePrefsToApi(prefsToStore)
}

function setPreferences(
  runtime: UserPreferencesRuntime,
  nextPrefs: UserPreferencesPayload,
  options?: { persist?: boolean },
): void {
  runtime.userPrefsStore.setPrefs(nextPrefs)
  if (options?.persist !== false) {
    persistPrefs(runtime, nextPrefs)
  }
}

function touchAndSet(runtime: UserPreferencesRuntime, partial: Partial<UserPreferencesPayload>): void {
  setPreferences(runtime, {
    ...runtime.prefs.value,
    ...partial,
    updatedAt: Date.now(),
  })
}

function resetToDefaults(runtime: UserPreferencesRuntime): void {
  const defaults = defaultUserPreferences()
  setPreferences(runtime, {
    ...defaults,
    welcomeSplashDone: runtime.prefs.value.welcomeSplashDone,
    onboarding: runtime.prefs.value.onboarding,
  })
}

function setOnboardingState(
  runtime: UserPreferencesRuntime,
  nextState: OnboardingStateMap,
  options?: { persist?: boolean },
): void {
  setPreferences(runtime, {
    ...runtime.prefs.value,
    updatedAt: Date.now(),
    onboarding: {
      version: 1,
      updatedAt: Date.now(),
      state: nextState,
    },
  }, options)
}

async function setWelcomeSplashDone(runtime: UserPreferencesRuntime, done: boolean): Promise<void> {
  const nextPreferences = withLatestOnboarding({
    ...runtime.prefs.value,
    updatedAt: Date.now(),
    welcomeSplashDone: done,
  })
  runtime.userPrefsStore.setPrefs(nextPreferences)
  writePrefsToLocalStorage(nextPreferences)
  writePrefsToCookie(nextPreferences, (value) => {
    runtime.prefsCookie.value = value
  })
  await putUserPreferencesToApi(nextPreferences)
}

async function hydrate(runtime: UserPreferencesRuntime): Promise<void> {
  if (import.meta.server) {
    return
  }
  if (clientHydrateInFlight !== null) {
    await clientHydrateInFlight
    return
  }
  clientHydrateInFlight = runHydrate(
    (ready) => {
      runtime.userPrefsStore.setReady(ready)
    },
    (loading) => {
      runtime.userPrefsStore.setLoading(loading)
    },
    (nextPrefs) => {
      runtime.userPrefsStore.setPrefs(nextPrefs)
      writePrefsToLocalStorage(nextPrefs)
    },
    () => ({
      loggedIn: runtime.loggedIn.value,
      accessToken: runtime.user.value?.accessToken,
    }),
    {
      readCookie: () => runtime.prefsCookie.value,
      writeCookie: (value) => {
        runtime.prefsCookie.value = value
      },
      applyColorMode: (preference) => applyColorModeFromPrefs(runtime, preference),
    },
  )
  try {
    await clientHydrateInFlight
  } finally {
    clientHydrateInFlight = null
  }
}

export function useUserPreferences() {
  const colorMode = useMonOrgaColorMode()
  const { loggedIn, user } = useOidcAuth()
  const userPrefsStore = useUserPrefsStore()
  const { prefs, isReady, isLoading } = storeToRefs(userPrefsStore)
  const isHydrateWatcherRegistered = useState<boolean>('monorga.userPrefs.hydrateWatcherRegistered', () => false)
  const prefsCookie = useCookie<UserPreferencesPayload | null>(USER_PREFS_COOKIE_KEY, {
    maxAge: USER_PREFS_COOKIE_MAX_AGE_SECONDS,
    sameSite: 'lax',
    secure: true,
    path: '/',
    default: () => null,
  })
  const runtime: UserPreferencesRuntime = {
    colorMode,
    loggedIn,
    user,
    userPrefsStore,
    prefs,
    prefsCookie,
  }

  if (import.meta.client && !userPrefsStore.isReady && !userPrefsStore.isLoading) {
    const bootstrapPrefs = preferNewestLocalPrefs(
      readPrefsFromCookie(prefsCookie.value),
      readPrefsFromLocalStorage(),
    )
    if (bootstrapPrefs !== null) {
      userPrefsStore.setPrefs(bootstrapPrefs)
      applyColorModeFromPrefs(runtime, bootstrapPrefs.colorModePreference)
    }
  }

  if (import.meta.client && !isHydrateWatcherRegistered.value) {
    isHydrateWatcherRegistered.value = true
    watch(
      () => ({ isLoggedIn: loggedIn.value, accessToken: user.value?.accessToken }),
      async () => {
        await hydrate(runtime)
      },
      { immediate: true },
    )
  }

  return {
    prefs,
    isReady,
    isLoading,
    hydrate: () => hydrate(runtime),
    setPreferences: (nextPrefs: UserPreferencesPayload, options?: { persist?: boolean }) =>
      setPreferences(runtime, nextPrefs, options),
    setOnboardingState: (nextState: OnboardingStateMap, options?: { persist?: boolean }) =>
      setOnboardingState(runtime, nextState, options),
    touchAndSet: (partial: Partial<UserPreferencesPayload>) => touchAndSet(runtime, partial),
    resetToDefaults: () => resetToDefaults(runtime),
    setWelcomeSplashDone: (done: boolean) => setWelcomeSplashDone(runtime, done),
  }
}
